import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../services/firebase';

const PlanContext = createContext();

export function usePlan() {
  return useContext(PlanContext);
}

const PLANS = {
  BASIC: 'BASIC',
  PRO: 'PRO'
};

const DEFAULT_PERMISSIONS = {
  // Permissões básicas que todos os usuários têm
  canAccessHabits: true,
  canAccessCalendar: true,
  canAccessMenu: true,
  canAccessSettings: true,
  canAccessHelp: true,

  // Permissões que variam de acordo com o plano
  canAccessMeditations: false,
  canAccessChallenges: false,
  canAccessGoals: false,
  canAccessNotes: false,
  canAccessProductivity: false,
  canAccessStatistics: false,
  
  // Permissões administrativas
  canAccessUserManagement: false
};

const PRO_PERMISSIONS = {
  ...DEFAULT_PERMISSIONS,
  canAccessMeditations: true,
  canAccessChallenges: true,
  canAccessGoals: true,
  canAccessNotes: true,
  canAccessProductivity: true,
  canAccessStatistics: true
};

export function PlanProvider({ children }) {
  const { currentUser } = useAuth();
  const [userPlan, setUserPlan] = useState(PLANS.BASIC);
  const [permissions, setPermissions] = useState(DEFAULT_PERMISSIONS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentUser) {
      setUserPlan(PLANS.BASIC);
      setPermissions(DEFAULT_PERMISSIONS);
      setLoading(false);
      return;
    }

    // Usar onSnapshot para ouvir mudanças em tempo real
    const unsubscribe = onSnapshot(doc(db, 'users', currentUser.uid), (doc) => {
      if (doc.exists()) {
        const userData = doc.data();
        
        // Verifica o role do usuário
        const userRole = userData.role || 'user';
        
        // Define o plano baseado no role
        if (userRole === 'admin') {
          setPermissions({
            ...PRO_PERMISSIONS,
            canAccessUserManagement: true
          });
          setUserPlan('PRO');
        } 
        else if (userRole === 'pro') {
          setPermissions(PRO_PERMISSIONS);
          setUserPlan('PRO');
        } 
        else {
          setPermissions(DEFAULT_PERMISSIONS);
          setUserPlan('BASIC');
        }
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [currentUser]);

  const updateUserPlan = (newPlan) => {
    setUserPlan(newPlan);
    setPermissions(newPlan === 'PRO' ? PRO_PERMISSIONS : DEFAULT_PERMISSIONS);
  };

  const value = {
    userPlan,
    permissions,
    loading,
    PLANS,
    updateUserPlan
  };

  return (
    <PlanContext.Provider value={value}>
      {!loading && children}
    </PlanContext.Provider>
  );
}

export default PlanProvider; 